import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

const AddMission: React.FC = () => {
  const navigate = useNavigate();
  const [missionData, setMissionData] = useState<any>({
    title: '',
    description: '',
    serviceType: '',
    subServiceType: '',
    scheduledDate: '',
    estimatedDuration: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    clientId: '',
    technicianId: '',
    quote: '',
    priority: 'normal',
  });
  const [quotes, setQuotes] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);
  const [subTypes, setSubTypes] = useState<string[]>([]);

  useEffect(() => {
    apiService.quotes.getAll().then(res => setQuotes(res.items));
    apiService.users.getAll({ role: 'client' }).then(res => setClients(res.items));
    apiService.users.getAll({ role: 'technician' }).then(res => setTechnicians(res.items));
    apiService.serviceTypes.getAll().then(res => setServiceTypes(res.items));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setMissionData(prev => ({ ...prev, [parent]: { ...prev[parent as keyof typeof prev], [child]: value } }));
    } else {
      setMissionData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.missions.create(missionData);
      toast.success('Mission créée avec succès');
      navigate('/admin/missions');
    } catch (error) {
      toast.error('Erreur lors de la création de la mission');
    }
  };

  return (
    <Layout>
      <Card className="p-6">
        <h1 className="text-2xl font-bold mb-4">Créer une mission</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">Titre</label>
              <input
                type="text"
                id="title"
                name="title"
                value={missionData.title}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700">Type de service</label>
              <select
                id="serviceType"
                name="serviceType"
                value={missionData.serviceType}
                onChange={(e) => {
                  handleChange(e);
                  const selectedServiceType = serviceTypes.find(st => st._id === e.target.value);
                  setSubTypes(selectedServiceType ? selectedServiceType.subTypes.map((st: any) => st.name) : []);
                }}
                className="w-full p-2 border rounded"
              >
                <option value="">Sélectionnez un type</option>
                {serviceTypes.map(st => (
                  <option key={st._id} value={st._id}>{st.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="subServiceType" className="block text-sm font-medium text-gray-700">Sous-type de service</label>
              <select
                id="subServiceType"
                name="subServiceType"
                value={missionData.subServiceType}
                onChange={handleChange}
                className="w-full p-2 border rounded"
                disabled={!missionData.serviceType || subTypes.length === 0}
              >
                <option value="">Sélectionnez un sous-type</option>
                {subTypes.map(sst => (
                  <option key={sst} value={sst}>{sst}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="scheduledDate" className="block text-sm font-medium text-gray-700">Date planifiée</label>
              <input
                type="date"
                id="scheduledDate"
                name="scheduledDate"
                value={missionData.scheduledDate}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label htmlFor="estimatedDuration" className="block text-sm font-medium text-gray-700">Durée estimée (heures)</label>
              <input
                type="number"
                id="estimatedDuration"
                name="estimatedDuration"
                value={missionData.estimatedDuration}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label htmlFor="clientId" className="block text-sm font-medium text-gray-700">Client</label>
              <select
                id="clientId"
                name="clientId"
                value={missionData.clientId}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              >
                <option value="">Sélectionnez un client</option>
                {clients.map(client => (
                  <option key={client._id} value={client._id}>{client.firstName} {client.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="technicianId" className="block text-sm font-medium text-gray-700">Technicien</label>
              <select
                id="technicianId"
                name="technicianId"
                value={missionData.technicianId}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              >
                <option value="">Sélectionnez un technicien</option>
                {technicians.map(tech => (
                  <option key={tech._id} value={tech._id}>{tech.firstName} {tech.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="quote" className="block text-sm font-medium text-gray-700">Devis</label>
              <select
                id="quote"
                name="quote"
                value={missionData.quote}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              >
                <option value="">Sélectionnez un devis</option>
                {quotes.map(quote => (
                  <option key={quote._id} value={quote._id}>{quote.reference} - {quote.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700">Priorité</label>
              <select
                id="priority"
                name="priority"
                value={missionData.priority}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              >
                <option value="low">Faible</option>
                <option value="normal">Normale</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              id="description"
              name="description"
              value={missionData.description}
              onChange={handleChange}
              rows={4}
              className="w-full p-2 border rounded"
            ></textarea>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">Adresse</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <input type="text" name="address.street" placeholder="Rue" value={missionData.address.street} onChange={handleChange} className="w-full p-2 border rounded" />
              <input type="text" name="address.city" placeholder="Ville" value={missionData.address.city} onChange={handleChange} className="w-full p-2 border rounded" />
              <input type="text" name="address.postalCode" placeholder="Code Postal" value={missionData.address.postalCode} onChange={handleChange} className="w-full p-2 border rounded" />
              <input type="text" name="address.country" placeholder="Pays" value={missionData.address.country} onChange={handleChange} className="w-full p-2 border rounded" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit">Créer la mission</Button>
          </div>
        </form>
      </Card>
    </Layout>
  );
};

export default AddMission;
